import {
  Button, Card, Input, Select, Space, Table, Tag, Typography,
} from 'antd';
import rideDemandApi from 'api/rideDemandApi';
import ToastHelper from 'general/helpers/ToastHelper';
import useRouter from 'hooks/useRouter';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  formatDemandDate, formatDemandMoney, getRideDemandErrorMessage, RideDemandStatusTag, STATUS_OPTIONS,
} from '../../rideDemandsConstants';

const { Text, Title } = Typography;

const EMPTY_CRITERIA = { status: '', demandCode: '', customerEmail: '' };

function RideDemandsListScreen() {
  const router = useRouter();
  const [filter, setFilter] = useState({ status: '', page: 1, pageSize: 20 });
  const [criteria, setCriteria] = useState(EMPTY_CRITERIA);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await rideDemandApi.getDemands(filter);
      setItems(response?.items || []);
      setTotal(response?.total || 0);
    } catch (error) {
      setItems([]);
      setTotal(0);
      ToastHelper.showError(getRideDemandErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const applyCriteria = () => setFilter((value) => ({ ...value, ...criteria, page: 1 }));
  const resetCriteria = () => {
    setCriteria(EMPTY_CRITERIA);
    setFilter((value) => ({ status: '', page: 1, pageSize: value.pageSize }));
  };

  const goToDetail = (demandId) => router.navigate(`${demandId}`);

  const columns = useMemo(() => [
    { title: 'Mã yêu cầu', dataIndex: 'demandId', render: (value) => <Text strong>{String(value).slice(0, 8)}</Text> },
    {
      title: 'Khách',
      render: (_, row) => (
        <div>
          <Text strong>{row.customer?.fullname || `User #${row.customer?.userId || ''}`}</Text>
          <br />
          <Text type="secondary">{row.customer?.phone || row.customer?.email || ''}</Text>
        </div>
      ),
    },
    {
      title: 'Đón/trả',
      render: (_, row) => (
        <div>
          {row.pickup?.address || '—'}
          <br />
          <Text type="secondary">
            →
            {' '}
            {row.dropoff?.address || '—'}
          </Text>
        </div>
      ),
    },
    {
      title: 'Giờ đón',
      render: (_, row) => formatDemandDate(row.departureFrom),
    },
    {
      title: 'Ghế',
      render: (_, row) => (row.isCharter
        ? <span>Xe {row.charterVehicleCapacity || 4} chỗ<Tag color="gold" style={{ marginLeft: 6 }}>Bao xe</Tag></span>
        : <span>{row.seats} ghế</span>),
    },
    {
      title: 'Giá cuối cùng',
      render: (_, row) => formatDemandMoney(row.fare == null
        ? null
        : Math.max(0, Number(row.fare.totalFare || 0) - Number(row.fare.promoDiscountAmount || 0))),
    },
    {
      title: 'Chuyến đã nhận',
      render: (_, row) => (row.matchedTripCode ? <Text>{row.matchedTripCode}</Text> : <Text type="secondary">—</Text>),
    },
    { title: 'Trạng thái', dataIndex: 'status', render: (value) => <RideDemandStatusTag status={value} /> },
    { title: 'Tạo lúc', dataIndex: 'createdAt', render: formatDemandDate },
    { title: '', render: (_, row) => <Button type="link" onClick={() => goToDetail(row.demandId)}>Chi tiết</Button> },
  ], []);

  return (
    <div className="container-fluid px-4 my-4">
      <Card>
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">
          <div>
            <Title level={4} className="mb-0">Yêu cầu hẹn giờ xe ghép</Title>
            <Text type="secondary">
              Tổng
              {' '}
              {total}
              {' '}
              yêu cầu
            </Text>
          </div>
          <Button onClick={load} loading={loading}>Làm mới</Button>
        </div>
        <Space wrap size={[12, 12]} style={{ marginBottom: 20 }}>
          <Input
            value={criteria.demandCode}
            placeholder="Mã yêu cầu"
            allowClear
            style={{ width: 200 }}
            onChange={(event) => setCriteria((value) => ({ ...value, demandCode: event.target.value }))}
            onPressEnter={applyCriteria}
          />
          <Input
            value={criteria.customerEmail}
            placeholder="Email khách hàng"
            allowClear
            style={{ width: 220 }}
            onChange={(event) => setCriteria((value) => ({ ...value, customerEmail: event.target.value }))}
            onPressEnter={applyCriteria}
          />
          <Select
            value={criteria.status}
            options={STATUS_OPTIONS}
            style={{ width: 200 }}
            onChange={(status) => setCriteria((value) => ({ ...value, status }))}
          />
          <Button type="primary" style={{ backgroundColor: '#30BD6F', borderColor: '#30BD6F' }} onClick={applyCriteria}>Lọc</Button>
          <Button onClick={resetCriteria}>Xóa lọc</Button>
        </Space>
        <Table
          rowKey="demandId"
          dataSource={items}
          columns={columns}
          loading={loading}
          scroll={{ x: 1300 }}
          locale={{ emptyText: 'Không có dữ liệu' }}
          pagination={{
            current: filter.page,
            pageSize: filter.pageSize,
            total,
            showSizeChanger: true,
            onChange: (page, pageSize) => setFilter((value) => ({ ...value, page, pageSize })),
          }}
          onRow={(row) => ({ onDoubleClick: () => goToDetail(row.demandId) })}
        />
      </Card>
    </div>
  );
}

export default RideDemandsListScreen;
